import { FC, useMemo } from 'react'
import { alpha, Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { CheckCircleOutline } from '@mui/icons-material'

import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useIntl } from 'react-intl'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useSchedulesWithDocumnets } from 'services/contracts/hooks/useSchedulesWithDocumnets'
import theme from 'styles/theme'

import { flattenScheduleGroups, formatMoney, getDocumentDueDate } from './leases.utils'

// Balance hero card ("Panel Klienta 3.0": Figma "Hero saldo").
const AdditionalServicesPaymentStatus: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { activeLeaseContractId } = useActiveLeaseContractId()

  const { accountBalance, schedulesWithDocumnets, isLoading } = useSchedulesWithDocumnets({
    activeLeaseContractId,
    isPaid: false
  })

  const { getActiveContract } = useLeaseContractsList()

  const activeContract = useMemo(
    () => getActiveContract(activeLeaseContractId),
    [activeLeaseContractId, getActiveContract]
  )

  const nextDueDate = useMemo(() => {
    const dates = flattenScheduleGroups(schedulesWithDocumnets)
      .filter((document) => !!document.number)
      .map((document) => getDocumentDueDate(document))
      .filter((date) => date.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf())

    return dates[0]
  }, [schedulesWithDocumnets])

  if (isLoading) {
    return null
  }

  const hasDebt = accountBalance > 0
  const currency = activeContract?.paymentCurrency ?? ''

  return (
    <Box
      sx={{
        background: theme.r4r.gradient,
        boxShadow: theme.r4r.cardShadow,
        padding: isDesktop ? '28px 32px' : '20px',
        mb: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
      <Box>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: alpha('#fff', 0.55),
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
          {formatMessage({
            id: 'additionalServicesPaymentStatus.balance',
            defaultMessage: 'Twoje saldo'
          })}
        </Typography>
        <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', mt: '4px', lineHeight: 1.1 }}>
          {hasDebt ? `-${formatMoney(accountBalance, currency)}` : formatMoney(0, currency)}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: alpha('#fff', 0.5), mt: '8px' }}>
          {hasDebt && nextDueDate
            ? formatMessage(
                {
                  id: 'additionalServicesPaymentStatus.dueDate',
                  defaultMessage: 'Termin płatności: {date}'
                },
                { date: nextDueDate.format('DD.MM.YYYY') }
              )
            : hasDebt
            ? formatMessage({
                id: 'additionalServicesPaymentStatus.toPay',
                defaultMessage: 'Do opłacenia'
              })
            : formatMessage({
                id: 'additionalServicesPaymentStatus.paid',
                defaultMessage: 'Opłacone'
              })}
        </Typography>
      </Box>
      {!hasDebt && (
        <Box
          sx={{
            width: '48px',
            height: '48px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#fff', 0.15)
          }}>
          <CheckCircleOutline sx={{ color: '#fff', fontSize: '26px' }} />
        </Box>
      )}
    </Box>
  )
}

export default AdditionalServicesPaymentStatus
